import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useAuth } from "./AuthProvider";
import environment from "../env";
import { useAccount } from "./AccountProvider";
import { useParty } from "./PartyProvider";
import { useUtils } from "./UtilsProvider";

const AnalyticsContext = createContext();

function uuidv4() {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
        (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4))).toString(16)
    );
}

const AnalyticsProvider = ({ children }) => {
    const { accessToken } = useAuth();
    const { useGetAccount } = useAccount();
    const { account } = useGetAccount();
    const { formState } = useUtils();
    const { useGetParty } = useParty();
    const { party } = useGetParty();

    const [queue, setQueue] = useState([]);
    const posting = useRef(false);

    const setTag = (tagValue) => {
        const path = window.location.pathname;
        setQueue((q) => [...q, { tag: tagValue, path }]);
  };

    const contextValue = useMemo(
        () => ({
          tag: typeof queue.at(-1)?.tag === "string" ? queue.at(-1)?.tag : undefined,
          setTag,
      }),
      [queue]
  );

    const postStringTag = async (tagValue, pathValue) => {
        const isLoginFailureTag =
            tagValue && typeof tagValue === "string" && tagValue.toLowerCase() === "login_idv_failed";
        const accountReference = account?.accountReference || formState?.accountReference || "unknown";
        const accountId = isLoginFailureTag ? (account?.accountId ?? 0) : (account?.accountId || 1);
        const partyId = isLoginFailureTag ? 0 : (party?.partyId || 1);
        const sessionId = localStorage.getItem("sessionId") || uuidv4();
        if (!localStorage.getItem("sessionId")) localStorage.setItem("sessionId", sessionId);
        const analyticsPayload = {
            tag: tagValue,
            accountReference,
            accountId,
            partyId,
            path: pathValue,
            sessionId,
            client: environment.CLIENT_NAME,
        };
        const res = await fetch(`${environment.CUSTOMER_PORTAL_API}/analytics`, {
            method: "POST",
            headers: {
                "x-api-key": environment.CUSTOMER_PORTAL_API_KEY,
                Authorization: `Bearer ${accessToken}`,
                organisation: environment.CLIENT_NAME,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(analyticsPayload),
        });
        return res.json();
  };

    const postCustomTag = async (customPayload) => {
        const res = await fetch(`${environment.CUSTOMER_PORTAL_API}/analytics`, {
            method: "POST",
            headers: {
                "x-api-key": environment.CUSTOMER_PORTAL_API_KEY,
                Authorization: `Bearer ${accessToken}`,
                organisation: environment.CLIENT_NAME,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(customPayload),
    });
      return res.json();
  };

    useEffect(() => {
        const processNext = async () => {
            if (posting.current || queue.length === 0) return;
        const next = queue.at(0);
        const rawTag = next.tag;
        const tagName = typeof rawTag === "string" ? rawTag : rawTag?.tag;
        const isFailureTag = typeof tagName === "string" && tagName.toLowerCase().includes("_failed");
        const accountReady = !!(account?.accountId && account?.accountReference);
        const partyReady = !!party?.partyId;
        if (!isFailureTag && !(accountReady && partyReady)) return;
        posting.current = true;
        try {
            if (typeof rawTag === "object") {
                await postCustomTag(rawTag);
                console.log("Analytics posted (custom):", rawTag);
            } else if (typeof rawTag === "string" && rawTag.trim() !== "") {
                await postStringTag(rawTag, next.path);
                console.log("Analytics posted:", rawTag);
        } else {
            console.warn("[Analytics] Skipped empty/invalid tag:", rawTag);
        }
      } catch (err) {
          console.error("Error posting analytics:", err);
      } finally {
          setQueue((q) => q.slice(1));
          posting.current = false;
      }
      };
      processNext();
  }, [queue, account, party]);

    return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
};

AnalyticsProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

const useAnalytics = () => useContext(AnalyticsContext);

export { AnalyticsProvider, useAnalytics };
